import { CLIENT_DATA_COLUMN_GROUP } from 'src/constants/clients';
import { computed } from 'vue';

import { useClientColumns } from './clientColumns';

/**
 * Client columns a filter vocabulary is built from: the ones named by the caller, in that order,
 * then every custom client data field. Shared by the cohort dialog and the Care Model report
 * filter, so the same criterion selects the same clients on both.
 *
 * @param {Object} params
 * @param {string[]} params.columnNames - Standard client columns to offer, in display order.
 * @param {string[]} [params.excludedClientDataFilterTypes] - Filter types to leave out of the
 *   custom fields, for a surface that cannot support them.
 * @returns {{
 *   columns: import('vue').ComputedRef<Array<Object>>,
 *   columnGroups: import('vue').ComputedRef<Array<Object>>,
 * }}
 */
// eslint-disable-next-line import/prefer-default-export
export function useClientFilterColumns({ columnNames = [], excludedClientDataFilterTypes = [] } = {}) {
  const { buildColumns, buildColumnGroups } = useClientColumns();

  const columns = computed(() => {
    const allColumns = buildColumns({ includeClientData: true });

    const baseColumns = columnNames
      .map((name) => allColumns.find((column) => column.name === name))
      .filter(Boolean);

    const clientDataColumns = allColumns.filter((column) => (
      column.group === CLIENT_DATA_COLUMN_GROUP
      && !excludedClientDataFilterTypes.includes(column.filterType)
    ));

    return [...baseColumns, ...clientDataColumns];
  });

  const columnGroups = computed(() => buildColumnGroups({ includeClientData: true }));

  return {
    columns,
    columnGroups,
  };
}
